import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * Full-screen overlay dialog for the avatar and product creators. In-page,
 * like every other dialog in the app — no browser popups.
 */
export default function CreatorModal({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-stone-900/40 backdrop-blur-sm p-4 md:p-8">
      {/* Click on the dark area closes; clicks inside the card don't bubble out. */}
      <div className="absolute inset-0" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className="relative zen-card w-full max-w-3xl p-6 md:p-8 my-4 space-y-6 bg-white"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-serif text-xl font-medium">{title}</h2>
            {subtitle && <p className="text-xs text-natural-text/55 mt-1">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl text-natural-text/50 hover:text-natural-text hover:bg-natural-border/20 transition-colors cursor-pointer shrink-0"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
